import { useCallback, useEffect, useState } from 'react';
import { Button, Popconfirm, Space, Table, Tag, Typography, notification } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useImpersonationService } from '../../services/impersonationService';

/** Session history (#790) with per-session revoke and the kill switch that ends every open session. */
export default function ImpersonationSessionsTable() {
    const { t } = useTranslation('impersonation');
    const { getSessions, revokeSession, revokeAllSessions } = useImpersonationService();
    const [api, contextHolder] = notification.useNotification();
    const [page, setPage] = useState(1);
    const [data, setData] = useState({ items: [], total: 0 });
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setData(await getSessions({ page, pageSize: 20 }));
        } catch (error) {
            // an empty table would read as "no open sessions": say the history could not be loaded instead
            api.error({ message: t('sessions.loadFailed'), placement: 'top' });
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    useEffect(() => {
        load();
    }, [load]);

    const revoke = async id => {
        try {
            await revokeSession(id);
        } catch (error) {
            // already ended
        }
        load();
    };

    // the kill switch must never fail silently: an admin would believe the open sessions were ended
    const revokeAll = async () => {
        try {
            const { revoked } = await revokeAllSessions();
            api.info({ message: t('sessions.revokedAll', { count: revoked }), placement: 'top' });
        } catch (error) {
            api.error({ message: t('sessions.revokeAllFailed'), placement: 'top' });
        } finally {
            load();
        }
    };

    const isOpen = row => !row.endedAt && dayjs(row.expiresAt).isAfter(dayjs());

    const columns = [
        {
            key: 'startedAt',
            title: t('sessions.started'),
            render: (_, row) => dayjs(row.startedAt).format('YYYY-MM-DD HH:mm'),
        },
        { key: 'actor', title: t('sessions.actor'), dataIndex: 'actorName' },
        {
            key: 'target',
            title: t('sessions.target'),
            render: (_, row) => `${row.targetName || '—'} (${t(`tabs.${row.targetRealm}`)} #${row.targetId})`,
        },
        {
            key: 'reason',
            title: t('sessions.reason'),
            dataIndex: 'reason',
            render: value => (
                <Typography.Text ellipsis={{ tooltip: value }} style={{ maxWidth: 240 }}>
                    {value}
                </Typography.Text>
            ),
        },
        {
            key: 'state',
            title: t('sessions.state'),
            render: (_, row) =>
                isOpen(row) ? (
                    <Tag color="red">{t('sessions.open')}</Tag>
                ) : (
                    <Tag>{t(`sessions.ended.${row.endReason || 'expired'}`)}</Tag>
                ),
        },
        { key: 'requests', title: t('sessions.requests'), dataIndex: 'requests' },
        {
            key: 'ip',
            title: t('sessions.ip'),
            render: (_, row) => [row.actorIp, row.exchangeIp].filter(Boolean).join(' → '),
        },
        {
            key: 'action',
            title: '',
            render: (_, row) =>
                isOpen(row) && (
                    <Button size="small" danger onClick={() => revoke(row.id)}>
                        {t('sessions.revoke')}
                    </Button>
                ),
        },
    ];

    return (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {contextHolder}
            <Popconfirm title={t('sessions.revokeAllConfirm')} onConfirm={revokeAll} okButtonProps={{ danger: true }}>
                <Button danger>{t('sessions.revokeAll')}</Button>
            </Popconfirm>
            <Table
                rowKey="id"
                size="middle"
                loading={loading}
                columns={columns}
                dataSource={data.items}
                scroll={{ x: 'max-content' }}
                pagination={{
                    current: page,
                    pageSize: 20,
                    total: data.total,
                    onChange: setPage,
                    showSizeChanger: false,
                }}
            />
        </Space>
    );
}
